import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface ConfirmStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isActive?: boolean;
  itemName?: string;
  entityName?: string; // e.g., 'coOwner', 'employee'
}

const ConfirmBlockDialog: React.FC<ConfirmStatusDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isActive = true,
  itemName = "",
  entityName = "user",
}) => {
  const action = isActive ? "Deactivate" : "Enable";
  const actionLower = isActive ? "deactivate" : "enable";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <div className="px-2 pt-5 pb-2 text-center">
        {/* Animated Icon */}
        <div className="flex justify-center">
          {isActive ? (
            <BlockIcon
              className="animate-pulse"
              color="error"
              sx={{
                fontSize: 48,
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                padding: "8px",
              }}
            />
          ) : (
            <CheckCircleIcon
              className="animate-pulse"
              color="success"
              sx={{
                fontSize: 48,
                backgroundColor: "#dcfce7",
                borderRadius: "50%",
                padding: "8px",
              }}
            />
          )}
        </div>

        <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: 700, textAlign: "center" }}>
          Confirm {action}
        </DialogTitle>

        <DialogContent
          sx={{
            maxHeight: "60vh",
            overflowY: "auto",
          }}
          className="scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400"
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to <strong>{actionLower}</strong>{" "}
            {itemName ? `${entityName} "${itemName}"` : `this ${entityName}`}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will update their status and access permissions.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={onClose}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={isActive ? "error" : "success"}
            startIcon={isActive ? <BlockIcon /> : <CheckCircleIcon />}
            onClick={onConfirm}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {action}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ConfirmBlockDialog;
